import React, { useState, useMemo, useCallback } from 'react';
import { GridColDef, GridRowsProp, GridRowSelectionModel } from '@mui/x-data-grid';
import {
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import { Edit, Check, Close } from '@mui/icons-material';
import { Traveller, Trip, Apartment } from '@/types';
import { travellerService } from '@/services/travellerService';
import EditTravellerForm from './EditTravellerForm';
import MemberStatusChip from '@/components/ui/MemberStatusChip';
import ReusableDataGrid from '@/components/ui/ReusableDataGrid';
import { calculateAge } from '@/utils/calculations';
import { useI18n } from '@/hooks/useI18n';

interface TravellerDataGridProps {
  trip: Trip;
  travellers: Traveller[];
  apartments: Apartment[];
}

interface MultiEditData {
  apartmentId?: string;
  skiPassType?: string;
  transportationType?: string;
}

const TravellerDataGrid: React.FC<TravellerDataGridProps> = ({
  trip,
  travellers,
  apartments,
}) => {
  const { skiPasses, transportationOptions } = trip;
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<string>(),
  });
  const [multiEditMode, setMultiEditMode] = useState(false);
  const [multiEditData, setMultiEditData] = useState<MultiEditData>({});
  const { t } = useI18n();

  const getApartmentName = useCallback((apartmentId?: string) => {
    const apartment = apartments.find(apt => apt.id === apartmentId);
    return apartment ? apartment.name : t('traveller.table.notAssigned');
  }, [apartments, t]);

  const getSkiPassName = useCallback((skiPassId?: string) => {
    const skiPass = skiPasses.find(pass => pass.id === skiPassId);
    return skiPass ? skiPass.name : t('traveller.table.notSelected');
  }, [skiPasses, t]);

  const getTransportationName = useCallback((transportationId?: string) => {
    const transportation = transportationOptions.find(t => t.id === transportationId);
    return transportation ? transportation.name : t('traveller.table.notSelected');
  }, [transportationOptions, t]);

  const applyMultiEdit = async () => {
    const selectedIds = Array.from(rowSelectionModel.ids);
    const updates = selectedIds.map(travellerId => {
      const traveller = travellers.find(t => t.id === travellerId);
      if (traveller) {
        const updatedTraveller: Partial<Traveller> = {};

        if (multiEditData.apartmentId !== undefined) {
          updatedTraveller.apartmentId = multiEditData.apartmentId === 'none' ? undefined : multiEditData.apartmentId;
        }
        if (multiEditData.skiPassType !== undefined) {
          updatedTraveller.skiPassType = multiEditData.skiPassType === 'none' ? undefined : multiEditData.skiPassType;
        }
        if (multiEditData.transportationType !== undefined) {
          updatedTraveller.transportationType = multiEditData.transportationType === 'none' ? undefined : multiEditData.transportationType;
        }

        if (Object.keys(updatedTraveller).length > 0) {
          return travellerService.updateTraveller(trip.id, traveller.id, updatedTraveller);
        }
      }
      return Promise.resolve();
    });

    await Promise.all(updates);

    setMultiEditMode(false);
    setRowSelectionModel({ type: 'include', ids: new Set() });
    setMultiEditData({});
  };

  const cancelMultiEdit = () => {
    setMultiEditMode(false);
    setRowSelectionModel({ type: 'include', ids: new Set() });
    setMultiEditData({});
  };

  // DataGrid columns definition
  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'name',
      headerName: `👤 ${t('traveller.table.name')}`,
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'email',
      headerName: `📧 ${t('traveller.table.email')}`,
      flex: 1.2,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'age',
      headerName: `🎂 ${t('traveller.table.age')}`,
      width: 100,
      valueGetter: (value, row) => calculateAge(row.dateOfBirth),
      renderCell: (params) => (
        <Typography variant="body2">
          {t('traveller.table.ageYears', { age: params.value })}
        </Typography>
      ),
    },
    {
      field: 'isSkiClubMember',
      headerName: `⛷️ ${t('traveller.table.skiClub')}`,
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <MemberStatusChip isMember={params.value} />
        </Box>
      ),
    },
    {
      field: 'apartmentId',
      headerName: `🏠 ${t('traveller.table.apartment')}`,
      flex: 1,
      minWidth: 150,
      valueGetter: (value) => getApartmentName(value),
    },
    {
      field: 'skiPassType',
      headerName: `🎿 ${t('traveller.table.skiPass')}`,
      flex: 1,
      minWidth: 150,
      valueGetter: (value) => getSkiPassName(value),
    },
    {
      field: 'transportationType',
      headerName: `🚌 ${t('traveller.table.transportation')}`,
      flex: 1,
      minWidth: 150,
      valueGetter: (value) => getTransportationName(value),
    },
    {
      field: 'actions',
      headerName: t('traveller.table.actions'),
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const traveller = params.row as Traveller;
        return (
          <EditTravellerForm 
            traveller={traveller} 
            trip={trip}
            trigger={
              <IconButton size="small">
                <Edit />
              </IconButton>
            }
          />
        );
      },
    },
  ], [t, trip, getApartmentName, getSkiPassName, getTransportationName]);

  // DataGrid rows preparation
  const rows: GridRowsProp = useMemo(() => 
    travellers.map((traveller) => ({
      ...traveller,
      id: traveller.id,
    })),
    [travellers]
  );

  const handleRowSelectionChange = useCallback((newSelectionModel: GridRowSelectionModel) => {
    setRowSelectionModel(newSelectionModel);
  }, []);

  return (
    <Box>
      {/* Multi-edit controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {rowSelectionModel.ids.size > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('traveller.table.selectedCount', { count: rowSelectionModel.ids.size })}
              </Typography>
              {!multiEditMode ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setMultiEditMode(true)}
                  startIcon={<Edit />}
                  sx={{ height: 32 }}
                >
                  {t('traveller.table.multiEdit')}
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    size="small" 
                    variant="contained" 
                    onClick={applyMultiEdit} 
                    startIcon={<Check />}
                    sx={{ height: 32 }}
                  >
                    {t('traveller.table.applyChanges')}
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    color="secondary" 
                    onClick={cancelMultiEdit} 
                    startIcon={<Close />}
                    sx={{ height: 32 }}
                  >
                    {t('traveller.table.cancel')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Multi-edit form */}
      {multiEditMode && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ backgroundColor: 'primary.lighter' }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              ✏️ {t('traveller.table.multiEditSelectedTravellers')}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>🏠 {t('traveller.table.apartment')}</InputLabel>
                  <Select
                    value={multiEditData.apartmentId || ''}
                    onChange={(e) => setMultiEditData({ ...multiEditData, apartmentId: e.target.value })}
                    label={`🏠 ${t('traveller.table.apartment')}`}
                  >
                    <MenuItem value="none">{t('traveller.table.noApartment')}</MenuItem>
                    {apartments.map((apartment) => (
                      <MenuItem key={apartment.id} value={apartment.id}>
                        {apartment.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>🎿 {t('traveller.table.skiPass')}</InputLabel>
                  <Select
                    value={multiEditData.skiPassType || ''}
                    onChange={(e) => setMultiEditData({ ...multiEditData, skiPassType: e.target.value })}
                    label={`🎿 ${t('traveller.table.skiPass')}`}
                  >
                    <MenuItem value="none">{t('traveller.table.noSkiPass')}</MenuItem>
                    {skiPasses.map((skiPass) => (
                      <MenuItem key={skiPass.id} value={skiPass.id}>
                        {skiPass.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>🚌 {t('traveller.table.transportation')}</InputLabel>
                  <Select
                    value={multiEditData.transportationType || ''}
                    onChange={(e) => setMultiEditData({ ...multiEditData, transportationType: e.target.value })}
                    label={`🚌 ${t('traveller.table.transportation')}`}
                  >
                    <MenuItem value="none">{t('traveller.table.noTransportation')}</MenuItem>
                    {transportationOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* DataGrid */}
      <ReusableDataGrid
        rows={rows}
        columns={columns}
        pageSize={25}
        rowHeight={60}
        getRowId={(row) => row.id}
        checkboxSelection={true}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={handleRowSelectionChange}
      />
    </Box>
  );
};

export default TravellerDataGrid;
